import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import UploadModal from './UploadModal';
import {
  FaArrowLeft,
  FaEye,
  FaDownload,
  FaSyncAlt,
  FaPlus,
  FaTrash,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';
import PreviewModal from './PreviewModal';
import { useAuth } from './useAuth';
import { useToast } from './ToastContext';

const ArrowLeftIcon = FaArrowLeft as unknown as React.FC<{ className?: string }>;
const TrashIcon = FaTrash as unknown as React.FC<{ className?: string }>;
const DownloadIcon = FaDownload as unknown as React.FC<{ className?: string }>;
const SyncIcon = FaSyncAlt as unknown as React.FC<{ className?: string }>;
const PlusIcon = FaPlus as unknown as React.FC<{ className?: string }>;
const ChevronDownIcon = FaChevronDown as unknown as React.FC<{ className?: string }>;
const ChevronRightIcon = FaChevronRight as unknown as React.FC<{ className?: string }>;

interface Category {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  sort_order: number;
  children: Category[];
}

interface DocumentVersion {
  id: string;
  version_number: number;
  original_filename: string;
  file_size_bytes: number;
  mime_type: string;
  has_preview: boolean;
}

interface DocumentItem {
  id: string;
  title: string;
  category_id: string;
  category_name: string;
  version_count: number;
  updated_at: string;
  modified_by: string | null;
  current_version: DocumentVersion | null;
  is_archived: boolean;
}

interface VersionItem {
  id: string;
  version_number: number;
  original_filename: string;
  file_size_bytes: number;
  mime_type: string;
  change_notes: string | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
  is_current: boolean;
}

const API_BASE = '/api';

function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryName = searchParams.get('name') || 'Documents';
  const activeChildId = searchParams.get('sub') || null;
  const navigate = useNavigate();

  const [children, setChildren] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalMode, setModalMode] = useState<'new' | 'replace' | null>(null);
  const [replaceTargetId, setReplaceTargetId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<{ id: string; title: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const { user } = useAuth();
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [versionsByDoc, setVersionsByDoc] = useState<Record<string, VersionItem[]>>({});
  const [replaceTargetTitle, setReplaceTargetTitle] = useState<string>('');
  const [restoreConfirm, setRestoreConfirm] = useState<{
    docId: string;
    versionId: string;
    versionNumber: number;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string } | null>(null);
  const { showToast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [reactivateConfirm, setReactivateConfirm] = useState<{ id: string; title: string } | null>(null);
  const [flatCategories, setFlatCategories] = useState<{ id: string; label: string }[]>([]);

  const confirmRestore = (docId: string, versionId: string, versionNumber: number) => {
    setRestoreConfirm({ docId, versionId, versionNumber });
  };

  const effectiveCategoryId = children.length > 0 ? activeChildId : categoryId;

  const loadDocuments = () => {
    const targetId = activeChildId || categoryId;
    if (!targetId) return;

    setLoading(true);
    const params = new URLSearchParams({ category_id: targetId });
    params.set('status', showArchived ? 'archived' : 'active');

    fetch(`${API_BASE}/documents?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => setDocuments(Array.isArray(data) ? data : []))
      .catch(() => setError('Could not load documents'))
      .finally(() => setLoading(false));
  };
  
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredDocuments = documents
    .filter((doc) =>
      doc.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }));

  const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE));

  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setError(null);

    fetch(`${API_BASE}/categories`)
      .then((res) => res.json())
      .then((allCategories: Category[]) => {
        const match = allCategories.find((c) => c.id === categoryId);
        const kids = match?.children || [];
        setChildren(kids);

        // default to the first sub-category if none is selected yet
        if (kids.length > 0 && !activeChildId) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('sub', kids[0].id);
            return next;
          });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    const targetId = activeChildId || categoryId;
    if (!targetId) return;

    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams({ category_id: targetId });
    params.set('status', showArchived ? 'archived' : 'active');

    fetch(`${API_BASE}/documents?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) {
          setDocuments(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load documents');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId, activeChildId, showArchived]);

  const goToSubCategory = (id: string, name: string) => {
    navigate(`/category/${id}?name=${encodeURIComponent(`${categoryName} - ${name}`)}`);
  };

  const openReplaceModal = (documentId: string, title: string) => {
    setReplaceTargetId(documentId);
    setReplaceTargetTitle(title);
    setModalMode('replace');
  };

  const closeModal = () => {
    setModalMode(null);
    setReplaceTargetId(null);
  };

  const confirmDelete = (documentId: string, title: string) => {
    setDeleteConfirm({ id: documentId, title });
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    const res = await fetch(`${API_BASE}/documents/${deleteConfirm.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    setDeleteConfirm(null);

    if (res.ok) {
      loadDocuments();
      showToast('Document inactivated successfully.');
    } else {
      showToast('Failed to inactivate document.', 'error');
    }
  };

  const fetchVersions = async (docId: string) => {
    const res = await fetch(`${API_BASE}/documents/${docId}/versions`, { credentials: 'include' });
    const data = await res.json();
    setVersionsByDoc((prev) => ({ ...prev, [docId]: Array.isArray(data) ? data : [] }));
  };

  const toggleExpand = async (docId: string) => {
    if (expandedDocId === docId) {
      setExpandedDocId(null);
      return;
    }
    setExpandedDocId(docId);
    if (!versionsByDoc[docId]) {
      await fetchVersions(docId);
    }
  };

  function flattenCategories(categories: Category[]): { id: string; label: string }[] {
    const result: { id: string; label: string }[] = [];
    for (const cat of categories) {
      if (cat.children.length === 0) {
        result.push({ id: cat.id, label: cat.name });
      } else {
        for (const child of cat.children) {
          result.push({ id: child.id, label: `${cat.name} - ${child.name}` });
        }
      }
    }
    return result;
  }

  useEffect(() => {
    fetch(`${API_BASE}/categories`)
      .then((res) => res.json())
      .then((allCategories: Category[]) => setFlatCategories(flattenCategories(allCategories)))
      .catch(() => {});
  }, []);

  const handleRestore = async () => {
    if (!restoreConfirm) return;
    const { docId, versionId, versionNumber } = restoreConfirm;

    const res = await fetch(`${API_BASE}/documents/${docId}/versions/${versionId}/restore`, {
      method: 'POST',
      credentials: 'include',
    });

    setRestoreConfirm(null);

    if (res.ok) {
      loadDocuments();
      fetchVersions(docId);
      showToast(`Restored to version ${versionNumber}.`);
    } else {
      showToast('Failed to restore version.', 'error');
    }
  };

 const confirmReactivate = (documentId: string, title: string) => {
    setReactivateConfirm({ id: documentId, title });
  };

  const handleReactivateDocument = async () => {
    if (!reactivateConfirm) return;

    const res = await fetch(`${API_BASE}/documents/${reactivateConfirm.id}/restore-document`, {
      method: 'POST',
      credentials: 'include',
    });

    setReactivateConfirm(null);

    if (res.ok) {
      showToast(`"${reactivateConfirm.title}" reactivated.`);
      loadDocuments();
    } else {
      showToast('Failed to reactivate document.', 'error');
    }
  };

  const toggleSelect = (docId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = paginatedDocuments.map((d) => d.id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) return;

    const res = await fetch(`${API_BASE}/documents/bulk-download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_ids: Array.from(selectedIds) }),
    });

    if (!res.ok) {
      showToast('Bulk download failed.', 'error');
      return;
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'documents.zip';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    showToast(`${selectedIds.size} document(s) downloaded.`);
    setSelectedIds(new Set());
  };

  return (
    <div className="app">
      <div className="top-bar">
        <Link to="/" className="back-button">
          <ArrowLeftIcon /> Back to Categories
        </Link>

        <input
          type="text"
          placeholder="Search this category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-bar search-bar-inline"
        />
      </div>

      <div className="page-header-row">
        <h1>{categoryName}</h1>
          {user && effectiveCategoryId && (
            <button className="btn-primary" onClick={() => setModalMode('new')}>
              <PlusIcon /> Upload New Document
            </button>
          )}
      </div>

      {children.length > 0 && (
        <div className="filter-tabs">
          {children.map((child) => (
            <button
              key={child.id}
              className={`filter-tab ${activeChildId === child.id ? 'active' : ''}`}
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('sub', child.id);
                  return next;
                });
                setCurrentPage(1);
                setSearchQuery('');
              }}
            >
              {child.name}
            </button>
          ))}
        </div>
      )}
      {user && (
        <div className="status-filter">
          <button
            className={`status-filter-option ${!showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(false)}
          >
            Active
          </button>
          <button
            className={`status-filter-option ${showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(true)}
          >
            Inactive
          </button>
        </div>
      )}

      {loading && <p>Loading...</p>}
      {error && <p className="error">{error}</p>}
      <div className="doc-table-wrapper category-table-wrapper">
        <table className="doc-table">
          <thead>
            <tr>
              {user && (
                <th className="col-checkbox">
                  <input
                    type="checkbox"
                    checked={paginatedDocuments.length > 0 && paginatedDocuments.every((d) => selectedIds.has(d.id))}
                    onChange={toggleSelectAllOnPage}
                  />
                </th>
              )}
              <th className="col-name">Name</th>
              <th className="col-date">Last Modified</th>
              <th className="col-modified-by">Modified By</th>
              <th className="col-version">Version</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDocuments.map((doc) => (
              <React.Fragment key={doc.id}>
                <tr className={doc.is_archived ? 'row-archived' : ''}>
                  {user && (
                    <td className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(doc.id)}
                        onChange={() => toggleSelect(doc.id)}
                      />
                    </td>
                  )}
                  <td className="col-name">
                    <span
                      className="doc-name doc-name-link"
                      onClick={() => setPreviewDoc({ id: doc.id, title: doc.title })}
                    >
                      {doc.title}
                    </span>
                  </td>
                  <td className="col-date">{new Date(doc.updated_at).toLocaleDateString()}</td>
                  <td className="col-modified-by">{doc.modified_by || '-'}</td>
                  <td className="col-version">v{doc.current_version?.version_number ?? '-'}</td>
                  <td className="col-actions">
                    <div className="action-icons">
                      <a href={`${API_BASE}/documents/${doc.id}/download`} className="icon-button" title="Download">
                        <DownloadIcon />
                      </a>
                      {user && (
                        doc.is_archived ? (
                          <button className="btn-secondary" onClick={() => confirmReactivate(doc.id, doc.title)}>
                              Reactivate
                            </button>
                        ) : (
                          <>
                            <button className="icon-button" title="Update" onClick={() => openReplaceModal(doc.id, doc.title)}>
                              <SyncIcon />
                            </button>
                            <button className="icon-button icon-button-danger" title="Inactive" onClick={() => confirmDelete(doc.id, doc.title)}>
                              <TrashIcon />
                            </button>
                            <button className="icon-button" title="Version History" onClick={() => toggleExpand(doc.id)}>
                              {expandedDocId === doc.id ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </td>
                </tr>

                {expandedDocId === doc.id && (
                  <tr>
                    <td colSpan={5} className="version-history-cell">
                      {!versionsByDoc[doc.id] ? (
                        <p>Loading version history...</p>
                      ) : versionsByDoc[doc.id].length === 0 ? (
                        <p>No version history.</p>
                      ) : (
                        <table className="version-history-table">
                          <thead>
                            <tr>
                              <th>Version</th>
                              <th>Uploaded</th>
                              <th>Uploaded By</th>
                              <th>Notes</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {versionsByDoc[doc.id].map((v) => (
                              <tr key={v.id}>
                                <td>v{v.version_number}</td>
                                <td>{new Date(v.uploaded_at).toLocaleDateString()}</td>
                                <td>{v.uploaded_by_name || '-'}</td>
                                <td>{v.change_notes || '-'}</td>
                                <td>
                                  {v.is_current ? (
                                    <span className="status-badge status-active">Current</span>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td>
                                  {!v.is_current && (
                                    <button
                                      className="text-button"
                                      onClick={() => confirmRestore(doc.id, v.id, v.version_number)}
                                    >
                                      Restore
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {paginatedDocuments.length === 0 && !loading && (
              <tr>
                <td colSpan={6}>No documents found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="doc-card-list">
          {paginatedDocuments.map((doc) => (
            <div className="doc-card" key={doc.id}>
              <div className="doc-card-header">
                <input
                  type="checkbox"
                  checked={selectedIds.has(doc.id)}
                  onChange={() => toggleSelect(doc.id)}
                />
                <span
                  className="doc-name doc-name-link"
                  onClick={() => setPreviewDoc({ id: doc.id, title: doc.title })}
                >
                  {doc.title}
                </span>
              </div>

              <div className="doc-card-meta">
                <span>Modified: {new Date(doc.updated_at).toLocaleDateString()}</span>
                <span>By: {doc.modified_by || '-'}</span>
                <span>Version: v{doc.current_version?.version_number ?? '-'}</span>
              </div>

              <div className="doc-card-actions">
                <a
                  href={`${API_BASE}/documents/${doc.id}/download`}
                  className="icon-button"
                  title="Download"
                >
                  <DownloadIcon />
                </a>
                {user && (
                  <>
                    <button className="icon-button" title="Update" onClick={() => openReplaceModal(doc.id, doc.title)}>
                      <SyncIcon />
                    </button>
                    <button className="icon-button icon-button-danger" title="Remove" onClick={() => confirmDelete(doc.id, doc.title)}>
                      <TrashIcon />
                    </button>
                    <button className="icon-button" title="Version History" onClick={() => toggleExpand(doc.id)}>
                      {expandedDocId === doc.id ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    </button>
                  </>
                )}
              </div>

              {expandedDocId === doc.id && (
                <div className="doc-card-versions">
                  {!versionsByDoc[doc.id] ? (
                    <p>Loading version history...</p>
                  ) : versionsByDoc[doc.id].length === 0 ? (
                    <p>No version history.</p>
                  ) : (
                    versionsByDoc[doc.id].map((v) => (
                      <div key={v.id} className="doc-card-version-row">
                        <span>v{v.version_number} — {new Date(v.uploaded_at).toLocaleDateString()}</span>
                        {v.is_current ? (
                          <span className="status-badge status-active">Current</span>
                        ) : (
                          <button className="text-button" onClick={() => confirmRestore(doc.id, v.id, v.version_number)}>
                            Restore
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              &larr; Prev
            </button>

            <span className="pagination-info">
              Page {currentPage} of {totalPages} ({filteredDocuments.length} documents)
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next &rarr;
            </button>
          </div>
        )}

        {modalMode === 'new' && effectiveCategoryId && (
          <UploadModal
            mode="new"
            categoryId={effectiveCategoryId}
            categories={flatCategories}
            onClose={closeModal}
            onSuccess={() => {
              loadDocuments();
              showToast('Document uploaded successfully.');
            }}
          />
        )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-topleft" onClick={() => setDeleteConfirm(null)} aria-label="Close">
              &times;
            </button>
            <h2>Inactivate Document?</h2>
            <p>
              Inactivate <strong>{deleteConfirm.title}</strong>? This can be restored later if needed.
            </p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleDelete}>Inactivate</button>
            </div>
          </div>
        </div>
      )}

      {restoreConfirm && (
        <div className="modal-overlay" onClick={() => setRestoreConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-topleft" onClick={() => setRestoreConfirm(null)} aria-label="Close">
              &times;
            </button>
            <h2>Restore Version {restoreConfirm.versionNumber}?</h2>
            <p>This will become the current version of this document.</p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={handleRestore}>Restore</button>
            </div>
          </div>
        </div>
      )}

      {reactivateConfirm && (
        <div className="modal-overlay" onClick={() => setReactivateConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-topleft" onClick={() => setReactivateConfirm(null)} aria-label="Close">
              &times;
            </button>
            <h2>Reactivate Document?</h2>
            <p>
              Bring back <strong>{reactivateConfirm.title}</strong>? It will reappear in the active document list.
            </p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setReactivateConfirm(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleReactivateDocument}>Reactivate</button>
            </div>
          </div>
        </div>
      )}

      {modalMode === 'replace' && replaceTargetId && (
        <UploadModal
          mode="replace"
          categoryId={categoryId || ''}
          documentId={replaceTargetId}
          documentTitle={replaceTargetTitle}
          onClose={closeModal}
          onSuccess={() => {
            loadDocuments();
            showToast('Document updated successfully.');
          }}
        />
      )}

      {previewDoc && (
        <PreviewModal
          documentId={previewDoc.id}
          title={previewDoc.title}
          onClose={() => setPreviewDoc(null)}
        />
      )}
      {user && selectedIds.size > 0 && (
        <div className="bulk-select-bar">
          <span>{selectedIds.size} selected</span>
          <button className="btn-primary" onClick={handleBulkDownload}>
            Download Selected (.zip)
          </button>
          <button className="btn-secondary" onClick={() => setSelectedIds(new Set())}>
            Clear Selection
          </button>
        </div>
      )}
    </div>
  );
}

export default CategoryPage;